import { getDb } from '$lib/db/db';
import { notificationRepo, planRepo, planSessionRepo, preferencesRepo, taskRepo, historyRepo } from '$lib/db/repo';
import type { HistoryEntry, NotificationSettings, Plan, PlanSession, Preferences, Task } from '$lib/domain/types';
import { normalizePreferences } from '$lib/domain/preferences';
import { normalizeNotificationSettings } from '$lib/domain/notifications';
import { createTombstonePayload, type SyncTable } from '$lib/db/sync';
import { syncEngine } from '$lib/stores/sync';

interface BackupPayload {
  version: number;
  timestamp: string;
  tasks: Task[];
  plans: Plan[];
  planSessions: PlanSession[];
  history: HistoryEntry[];
  preferences: Preferences;
  notificationSettings: NotificationSettings;
}

type ImportRecordTable = Extract<SyncTable, 'tasks' | 'plans' | 'plan_sessions' | 'history'>;
type BackupSyncQueueItem = { table: SyncTable; id: string; payload: unknown };
type ExistingBackupIds = Record<ImportRecordTable, Set<string>>;
const MAX_BACKUP_BYTES = 5 * 1024 * 1024;

const emptyExistingIds = (): ExistingBackupIds => ({
  tasks: new Set(),
  plans: new Set(),
  plan_sessions: new Set(),
  history: new Set()
});

const recordsById = <T extends { id?: string }>(values: T[] = []) => {
  const records = new Map<string, T>();
  for (const value of values) {
    if (typeof value.id === 'string' && value.id.length > 0) records.set(value.id, value);
  }
  return records;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const assertRecordsHaveIds = (table: ImportRecordTable, records: unknown[]) => {
  for (const record of records) {
    if (!isRecord(record) || typeof record.id !== 'string' || record.id.length === 0) {
      throw new Error(`Backup contains an invalid ${table} record.`);
    }
  }
};

export const validateBackupPayload = (data: BackupPayload) => {
  if (!data || data.version !== 4) throw new Error('Invalid Focus Flow v4 backup.');
  if (!Array.isArray(data.tasks) || !Array.isArray(data.plans) || !Array.isArray(data.planSessions) || !Array.isArray(data.history)) {
    throw new Error('Backup is missing required data arrays.');
  }

  assertRecordsHaveIds('tasks', data.tasks);
  assertRecordsHaveIds('plans', data.plans);
  assertRecordsHaveIds('plan_sessions', data.planSessions);
  assertRecordsHaveIds('history', data.history);
};

const enqueueDeletedRecords = (
  queue: BackupSyncQueueItem[],
  table: ImportRecordTable,
  existingIds: Set<string>,
  incomingIds: Set<string>,
  deletedAt: string
) => {
  for (const id of existingIds) {
    if (!incomingIds.has(id)) queue.push({ table, id, payload: createTombstonePayload(id, deletedAt) });
  }
};

export const buildBackupImportSyncQueue = (
  data: BackupPayload,
  existingIds: ExistingBackupIds = emptyExistingIds(),
  deletedAt = data.timestamp || new Date().toISOString()
): BackupSyncQueueItem[] => {
  const queue: BackupSyncQueueItem[] = [];
  const tasks = recordsById(data.tasks ?? []);
  const plans = recordsById(data.plans ?? []);
  const planSessions = recordsById((data.planSessions ?? []).filter((session) => plans.has(session.planId)));
  const history = recordsById((data.history ?? []).filter((entry) => !entry.planId || plans.has(entry.planId)));
  const incoming = {
    tasks: new Set(tasks.keys()),
    plans: new Set(plans.keys()),
    plan_sessions: new Set(planSessions.keys()),
    history: new Set(history.keys())
  };

  for (const [id, task] of tasks) queue.push({ table: 'tasks', id, payload: task });
  for (const [id, plan] of plans) queue.push({ table: 'plans', id, payload: plan });
  for (const [id, session] of planSessions) queue.push({ table: 'plan_sessions', id, payload: session });
  for (const [id, entry] of history) queue.push({ table: 'history', id, payload: entry });

  enqueueDeletedRecords(queue, 'tasks', existingIds.tasks, incoming.tasks, deletedAt);
  enqueueDeletedRecords(queue, 'plans', existingIds.plans, incoming.plans, deletedAt);
  enqueueDeletedRecords(queue, 'plan_sessions', existingIds.plan_sessions, incoming.plan_sessions, deletedAt);
  enqueueDeletedRecords(queue, 'history', existingIds.history, incoming.history, deletedAt);

  queue.push({ table: 'preferences', id: 'default', payload: { key: 'default', value: normalizePreferences(data.preferences) } });
  queue.push({
    table: 'notification_settings',
    id: 'default',
    payload: { key: 'default', value: normalizeNotificationSettings(data.notificationSettings) }
  });

  return queue;
};

export const buildBackup = async (): Promise<BackupPayload> => {
  const tasks = await taskRepo.list();
  const plans = await planRepo.list();
  const sessionsNested = await Promise.all(plans.map((plan) => planSessionRepo.list(plan.id)));
  const planSessions = sessionsNested.flat();
  const history = await historyRepo.list();
  const preferences = await preferencesRepo.get();
  const notificationSettings = await notificationRepo.get();

  return {
    version: 4,
    timestamp: new Date().toISOString(),
    tasks,
    plans,
    planSessions,
    history,
    preferences,
    notificationSettings
  };
};

export const downloadBackup = async () => {
  const payload = await buildBackup();
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `focus-flow-backup-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const importBackup = async (file: File) => {
  if (typeof file.size === 'number' && file.size > MAX_BACKUP_BYTES) throw new Error('Backup file is too large.');
  const content = await file.text();
  const data = JSON.parse(content) as BackupPayload;
  validateBackupPayload(data);

  const preferences = normalizePreferences(data.preferences);
  const notificationSettings = normalizeNotificationSettings(data.notificationSettings);

  const db = await getDb();
  const existingIds: ExistingBackupIds = {
    tasks: new Set((await db.getAllKeys('tasks')).map(String)),
    plans: new Set((await db.getAllKeys('plans')).map(String)),
    plan_sessions: new Set((await db.getAllKeys('plan_sessions')).map(String)),
    history: new Set((await db.getAllKeys('history')).map(String))
  };

  const tx = db.transaction(
    ['tasks', 'plans', 'plan_sessions', 'history', 'preferences', 'notification_settings'],
    'readwrite'
  );
  await Promise.all([
    tx.objectStore('tasks').clear(),
    tx.objectStore('plans').clear(),
    tx.objectStore('plan_sessions').clear(),
    tx.objectStore('history').clear(),
    tx.objectStore('preferences').clear(),
    tx.objectStore('notification_settings').clear()
  ]);

  for (const task of data.tasks ?? []) {
    await tx.objectStore('tasks').put(task);
  }
  for (const plan of data.plans ?? []) {
    await tx.objectStore('plans').put(plan);
  }
  for (const session of data.planSessions ?? []) {
    await tx.objectStore('plan_sessions').put(session);
  }
  for (const entry of data.history ?? []) {
    await tx.objectStore('history').put(entry);
  }
  await tx.objectStore('preferences').put({ key: 'default', value: preferences });
  await tx.objectStore('notification_settings').put({ key: 'default', value: notificationSettings });

  await tx.done;

  const queued = buildBackupImportSyncQueue({ ...data, preferences, notificationSettings }, existingIds);

  for (const item of queued) {
    await syncEngine.enqueueChange(item.table, item.id, item.payload);
  }
};
